import React, { useEffect, useState } from 'react';
import useScrollLock from '../utils/useScrollLock';
import { isValidName, cleanName } from '../utils/name';
import { APP } from '../config/app';

/**
 * Bottom sheet: far pickup / out-of-town booker → self vs someone else + rider details.
 */
export default function BookForSomeoneSheet({
  open,
  t,
  distanceKm,
  reason, // 'farPickup' | 'outOfTown'
  onClose,
  onForSelf,
  onForOther,
}) {
  const [step, setStep] = useState('ask'); // ask | form
  const [riderName, setRiderName] = useState('');
  const [riderPhone, setRiderPhone] = useState('');
  const [error, setError] = useState('');
  useScrollLock(open);

  useEffect(() => {
    if (open) return undefined;
    setStep('ask');
    setRiderName('');
    setRiderPhone('');
    setError('');
    return undefined;
  }, [open]);

  if (!open) return null;

  const distLabel = Number.isFinite(distanceKm)
    ? (Math.round(distanceKm * 10) / 10).toFixed(1)
    : null;

  const body = reason === 'outOfTown'
    ? t('book.forOtherOutOfTown', { city: APP.city })
    : t('book.forOtherFarPickup', { km: distLabel || '1+' });

  const submitOther = () => {
    const name = cleanName(riderName);
    const phone = riderPhone.replace(/\D/g, '').slice(0, 10);
    if (!isValidName(name)) {
      setError(t('book.riderNameRequired'));
      return;
    }
    if (phone.length !== 10) {
      setError(t('book.riderPhoneRequired'));
      return;
    }
    setError('');
    onForOther({ riderName: name, riderPhone: phone });
    setStep('ask');
    setRiderName('');
    setRiderPhone('');
  };

  return (
    <div className="bfse-overlay" role="presentation" onClick={onClose}>
      <div
        className="bfse-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bfse-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bfse-handle" aria-hidden />
        {step === 'ask' ? (
          <>
            <h2 id="bfse-title" className="bfse-title">{t('book.forOtherTitle')}</h2>
            <p className="bfse-body">{body}</p>
            <button type="button" className="bfse-btn bfse-btn-primary" onClick={onForSelf}>
              {t('book.forMyself')}
            </button>
            <button
              type="button"
              className="bfse-btn bfse-btn-secondary"
              onClick={() => { setStep('form'); setError(''); }}
            >
              {t('book.forSomeoneElse')}
            </button>
          </>
        ) : (
          <>
            <h2 id="bfse-title" className="bfse-title">{t('book.riderDetailsTitle')}</h2>
            <p className="bfse-body">{t('book.riderDetailsHint')}</p>
            <label className="bfse-label">{t('book.riderName')}</label>
            <input
              className="bfse-input"
              type="text"
              autoComplete="name"
              placeholder={t('book.riderNamePlaceholder')}
              value={riderName}
              onChange={(e) => setRiderName(e.target.value.replace(/[^\p{L}\p{M}\s.'-]/gu, '').slice(0, 40))}
              autoFocus
            />
            <label className="bfse-label">{t('book.riderPhone')}</label>
            <div className="bfse-phone-row">
              <span className="bfse-prefix">+91</span>
              <input
                className="bfse-input"
                type="tel"
                inputMode="numeric"
                maxLength={10}
                autoComplete="tel-national"
                placeholder={t('book.riderPhonePlaceholder')}
                value={riderPhone}
                onChange={(e) => setRiderPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              />
            </div>
            {error ? <p className="bfse-error">{error}</p> : null}
            <button type="button" className="bfse-btn bfse-btn-primary" onClick={submitOther}>
              {t('book.riderContinue')}
            </button>
            <button type="button" className="bfse-btn bfse-btn-ghost" onClick={() => setStep('ask')}>
              {t('book.back')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
